import axios from "axios";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Strip a leading `www.` from the backend host.
 *
 * Emergent custom domains redirect `www.<domain>` → apex `<domain>` with
 * HTTP 308. That's fine for browsers loading HTML, but it BREAKS API calls
 * because browsers refuse to follow redirects on CORS preflight (OPTIONS)
 * requests. The preflight fails silently and axios reports a generic
 * network error — the user just sees "something went wrong".
 *
 * Normalising to the apex here means the browser talks directly to the
 * canonical host and CORS preflight succeeds, regardless of whether the
 * deployment env baked in the `www` variant.
 */
function normalizeBackendUrl(u) {
  if (!u) return u;
  try {
    const url = new URL(u);
    if (url.hostname.startsWith("www.")) {
      url.hostname = url.hostname.slice(4);
    }
    return url.origin;
  } catch {
    return u;
  }
}

const BACKEND_URL = normalizeBackendUrl(RAW_BACKEND_URL);
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 20000 });

const TOKEN_KEY = "keplerio_uat_token";
const PRODUCT_KEY = "klystic_product";

export const authStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

function currentProduct() {
  try {
    return localStorage.getItem(PRODUCT_KEY) || null;
  } catch {
    return null;
  }
}

client.interceptors.request.use((cfg) => {
  const t = authStore.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      authStore.clear();
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/signup")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  verify2FASetup: (payload) =>
    client.post("/auth/verify-2fa-setup", payload).then((r) => r.data),
  reset2FA: (payload) => client.post("/auth/reset-2fa", payload).then((r) => r.data),
  resetPassword: (payload) =>
    client.post("/auth/reset-password", payload).then((r) => r.data),
  login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),

  // Admin
  adminListUsers: () => client.get("/admin/users").then((r) => r.data),
  adminAddUser: (payload) => client.post("/admin/users", payload).then((r) => r.data),
  adminBlockUser: (id) => client.patch(`/admin/users/${id}/block`).then((r) => r.data),
  adminUnblockUser: (id) => client.patch(`/admin/users/${id}/unblock`).then((r) => r.data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),

  heartbeat: (session_id) =>
    client.post("/presence/heartbeat", { session_id }).then((r) => r.data),
  presenceActive: () => client.get("/presence/active").then((r) => r.data),

  // Product-aware endpoints — pass explicit `product` when known.
  listProducts: () => client.get("/products").then((r) => r.data),
  getChecklist: (product) =>
    client.get("/checklist", { params: { product: product || currentProduct() || "keplerio" } })
      .then((r) => r.data),
  createSession: (payload) =>
    client.post("/sessions", {
      ...payload,
      product: payload.product || currentProduct() || "keplerio",
    }).then((r) => r.data),
  listSessions: (product) =>
    client.get("/sessions", { params: { product: product || currentProduct() } })
      .then((r) => r.data),
  getSession: (id) => client.get(`/sessions/${id}`).then((r) => r.data),
  addResult: (id, payload) =>
    client.post(`/sessions/${id}/results`, payload).then((r) => r.data),
  completeSession: (id, payload) =>
    client.post(`/sessions/${id}/complete`, payload).then((r) => r.data),
  deleteSession: (id) => client.delete(`/sessions/${id}`).then((r) => r.data),

  downloadSessionCsv: (id) =>
    client.get(`/sessions/${id}/export.csv`, { responseType: "blob" }),
  downloadSessionPdf: (id) =>
    client.get(`/sessions/${id}/export.pdf`, { responseType: "blob" }),
  getConsolidated: (session_ids = null, product = null) =>
    client.post("/consolidated", {
      session_ids,
      product: product || currentProduct(),
    }).then((r) => r.data),
  downloadConsolidatedCsv: (session_ids = null, product = null) =>
    client.post("/consolidated/export.csv", {
      session_ids,
      product: product || currentProduct(),
    }, { responseType: "blob" }),
  downloadConsolidatedPdf: (session_ids = null, product = null) =>
    client.post("/consolidated/export.pdf", {
      session_ids,
      product: product || currentProduct(),
    }, { responseType: "blob" }),
};

export function saveBlobAs(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
